<template>
  <div id="app" :style="pcDialogScaleStyle">
    <!-- PC 端大厅: 移动端竖屏样式 + 固定比例容器 (横版尚未做, 全部走 portrait) -->
    <!-- 视讯 (/live/*) 有独立的 PC 布局, 不套这个手机壳, 见 isLiveRoute 定义处注释 -->
    <PortraitFrame v-if="isPCMode && !isLiveRoute">
      <router-view v-slot="{ Component }">
        <keep-alive>
          <component :is="Component" v-if="route.meta.keepAlive" :key="pageRenderKey" />
        </keep-alive>
        <component :is="Component" v-if="!route.meta.keepAlive" :key="pageRenderKey" />
      </router-view>
    </PortraitFrame>

    <!-- 移动端 / PC 端视讯: 不套固定比例容器, 各自页面自己做响应式布局 -->
    <!-- <MobileAspectRatioContainer v-else> -->
    <router-view v-else v-slot="{ Component }">
      <keep-alive>
        <component :is="Component" v-if="route.meta.keepAlive" :key="pageRenderKey" />
      </keep-alive>
      <component :is="Component" v-if="!route.meta.keepAlive" :key="pageRenderKey" />
    </router-view>
    <!-- </MobileAspectRatioContainer> -->
    <!-- 全局 loading 遮罩 (登录页不显示) -->
    <GlobalLoading v-if="!isLoginPage" />
    <!-- PWA 安装教程弹窗 (仅 iOS 设备) -->
    <PWAGuideDialog v-model="showPWAGuide" />
    <!-- 全局设置侧边栏 (只在大厅页面和游戏页面显示) -->
    <SettingsSidebar v-if="showSettingsSidebar" />

    <!-- 预测市场 (provider_code='pm') 全屏 iframe 覆盖层: 挂在 App.vue 全局而不是某个
         具体页面里, 是因为刷新页面后 tryRecoverPmSession() 要能在任意页面恢复会话,
         覆盖层也得跟着全局可用, 不能依赖某个特定页面组件还活着 -->
    <GameIframeOverlay :url="pmIframeUrl" close-text="退出" @close="exitPm" />

    <!-- 全局设置弹窗 (所有页面共用) -->
    <el-dialog
      v-model="showReportDialog"
      :lock-scroll="true"
      :modal="true"
      :width="bigDialogWidth"
      destroy-on-close
      class="report"
      :class="{
        'landscape-fullscreen': isLandscapeMode,
        'portrait-fullscreen': !isLandscapeMode,
        'pc-fullscreen': isPCMode,
      }"
    >
      <BetReport />
    </el-dialog>
    <el-dialog
      v-model="showRulesDialog"
      :lock-scroll="true"
      :modal="true"
      :top="isPCMode ? '0' : isLandscapeMode ? '0' : '10%'"
      :title="$t('menu_rules')"
      :width="bigDialogWidth"
      destroy-on-close
      :class="{ 'landscape-fullscreen': isLandscapeMode, 'pc-fullscreen': isPCMode }"
    >
      <GameRules />
    </el-dialog>
    <el-dialog
      v-model="showCustomBetDialog"
      :lock-scroll="false"
      :modal="true"
      :title="$t('menu_custom_bet')"
      :width="dialogWidth"
      destroy-on-close
    >
      <CustomBet @confirm="() => (showCustomBetDialog = false)" />
    </el-dialog>
    <el-dialog
      v-model="showBGMDialog"
      :lock-scroll="false"
      :modal="true"
      :title="$t('menu_music')"
      :width="smallDialogWidth"
      destroy-on-close
    >
      <BGMSetting @confirm="() => (showBGMDialog = false)" />
    </el-dialog>
    <el-dialog
      v-model="showVideoLineDialog"
      :lock-scroll="false"
      :modal="true"
      :title="$t('menu_video_line')"
      :width="smallDialogWidth"
      destroy-on-close
    >
      <VideoLine @confirm="() => (showVideoLineDialog = false)" />
    </el-dialog>
    <el-dialog
      v-model="showSwitchSettingsDialog"
      :lock-scroll="false"
      :modal="true"
      :title="$t('menu_settings')"
      :width="smallDialogWidth"
      destroy-on-close
    >
      <SwitchSettings />
    </el-dialog>
    <el-dialog
      v-model="showCommandHelpDialog"
      :lock-scroll="false"
      :modal="true"
      :title="$t('menu_command')"
      :width="dialogWidth"
      destroy-on-close
    >
      <CommandHelp />
    </el-dialog>
    <el-dialog
      v-model="showChangePasswordDialog"
      :lock-scroll="false"
      :modal="true"
      :title="$t('menu_change_password')"
      :width="dialogWidth"
      destroy-on-close
    >
      <ChangePassword @confirm="() => (showChangePasswordDialog = false)" />
    </el-dialog>
    <el-dialog
      v-model="showContactServiceDialog"
      :lock-scroll="false"
      :modal="true"
      :title="$t('menu_contact')"
      :width="dialogWidth"
      destroy-on-close
    >
      <ContactService />
    </el-dialog>

    <!-- 版本更新提示弹窗 -->
    <VersionUpdateDialog ref="versionUpdateDialogRef" @refresh="handleVersionRefresh" />

    <!-- 被踢下线提示弹窗 (账号在其他设备登录) -->
    <KickedOutDialog />

    <!-- 全局 Toast 提示 -->
    <GlobalToast />
  </div>
</template>

<script setup lang="ts">
import { useRoute } from 'vue-router'
import { LIVE_CONTENT_ROUTE_NAMES } from '@/router'
import GlobalLoading from '@/components/GlobalLoading.vue'
import GlobalToast from '@/components/GlobalToast.vue'
import PWAGuideDialog from '@/components/PWAGuideDialog.vue'
import PortraitFrame from '@/components/PortraitFrame.vue'
// import MobileAspectRatioContainer from '@/components/MobileAspectRatioContainer.vue'
import SettingsSidebar from '@/components/SettingsSidebar.vue'
import GameIframeOverlay from '@/components/GameIframeOverlay.vue'
import { usePmGamePlay, exitPm, tryRecoverPmSession } from '@/composables/usePmGamePlay'
import VersionUpdateDialog from '@/components/VersionUpdateDialog.vue'
import KickedOutDialog from '@/components/KickedOutDialog.vue'
import { useSettingsStore, useUserStore, useLobbyStore } from '@/stores'
import { useBgMusic } from '@/composables/useBgMusic'
// import { setupDevToolsProtection } from '@/utils/devToolsProtection'
import { useSettingsDialogs } from '@/composables/useSettingsDialogs'
import { useDeviceDetect } from '@/composables/useDeviceDetect'
import { useNetworkStatus } from '@/composables/useNetworkStatus'
import { useHeartbeat } from '@/composables/useHeartbeat'
import { setVersionUpdateCallback, handleVersionRefresh as configHandleVersionRefresh } from '@/services/pollManager'

// 设置弹窗组件 —— 全部是视讯专属功能 (且多数牵连整个 WS 协议收发代码),
// 静态 import 会把它们连同 services/websocket/* 一起打进大厅也会加载的 App.vue 主入口 chunk,
// 改成 defineAsyncComponent 后配合下面 el-dialog 的 destroy-on-close, 只有真正点开对应弹窗才会去下载
import { defineAsyncComponent } from 'vue'
const BetReport = defineAsyncComponent(() => import('@/views/home/components/report/BetReport.vue'))
const GameRules = defineAsyncComponent(() => import('@/views/home/components/rules/GameRules.vue'))
const CustomBet = defineAsyncComponent(() => import('@/views/home/components/chips/CustomBet.vue'))
const BGMSetting = defineAsyncComponent(() => import('@/views/home/components/music/BGMSetting.vue'))
const VideoLine = defineAsyncComponent(() => import('@/views/home/components/video/VideoLine.vue'))
const SwitchSettings = defineAsyncComponent(() => import('@/views/home/components/settings/SwitchSettings.vue'))
const CommandHelp = defineAsyncComponent(() => import('@/views/home/components/command/CommandHelp.vue'))
const ChangePassword = defineAsyncComponent(() => import('@/views/home/components/password/ChangePassword.vue'))
const ContactService = defineAsyncComponent(() => import('@/views/home/components/service/ContactService.vue'))

const route = useRoute()
const settingsStore = useSettingsStore()

// 预测市场 (provider_code='pm') 覆盖层地址: 模块级共享状态, 入口在
// MainLobbyMobile.vue 的 goProvider(), 这里只负责渲染 + 开机恢复
const { pmIframeUrl } = usePmGamePlay()

// 设备检测
const { isMobileLandscape, isIPadMode, isLandscape, isPCMode } = useDeviceDetect()

// 视讯 (/live/*) 有自己的 PC/平板/手机独立布局 (GamePC.vue/GameMobile.vue 等),
// 不像大厅只有手机版设计——PC 端打开视讯时不能再套 PortraitFrame 那个手机壳
// (430x932 固定竖屏盒子), 否则 PC 版视讯布局会被硬塞进手机比例的画框里,
// 看起来是"手机壳里挤了个 PC 页面"。壳只用于大厅这类没有独立 PC 设计的页面。
const isLiveRoute = computed(() => route.path.startsWith('/live'))

// 是否是横屏模式
const isLandscapeMode = computed(() => {
  return isMobileLandscape.value || (isIPadMode.value && isLandscape.value)
})

// 弹窗宽度 (PC 版使用较小宽度)
const bigDialogWidth = computed(() => {
  if (isPCMode.value) return '1600px'
  return '90%'
})

// 弹窗宽度 (PC 版使用较小宽度)
const dialogWidth = computed(() => {
  if (isPCMode.value) return '700px'
  return '90%'
})

// 小弹窗宽度 (音乐、视频线路等)
const smallDialogWidth = computed(() => {
  if (isPCMode.value) return '600px'
  if (isLandscapeMode.value) return '50%'
  return '90%'
})

// ===================== PC 端弹窗缩放逻辑 =====================
// PC 端设计基准尺寸 (与 PcAspectRatioContainer 保持一致)
const DESIGN_WIDTH = 1920
const DESIGN_HEIGHT = 1080

// 窗口尺寸 (响应式)
const windowWidth = ref(window.innerWidth)
const windowHeight = ref(window.innerHeight)

/**
 * 计算 PC 端缩放比例 (与 PcAspectRatioContainer 逻辑一致)
 * 最大值限制为 1，避免弹窗被放大
 */
const pcScale = computed(() => {
  const scaleX = windowWidth.value / DESIGN_WIDTH
  const scaleY = windowHeight.value / DESIGN_HEIGHT
  return Math.min(scaleX, scaleY, 1)
})

/**
 * PC 端弹窗缩放样式
 * 通过 CSS 变量传递给全局样式
 */
const pcDialogScaleStyle = computed(() => {
  if (isPCMode.value) {
    return {
      '--pc-dialog-scale': pcScale.value,
    }
  }
  return {}
})

/**
 * 监听窗口大小变化 (更新缩放比例)
 */
const handleWindowResize = () => {
  windowWidth.value = window.innerWidth
  windowHeight.value = window.innerHeight
}

// 全局设置弹窗状态
const {
  showReportDialog,
  showRulesDialog,
  showCustomBetDialog,
  showBGMDialog,
  showVideoLineDialog,
  showSwitchSettingsDialog,
  showCommandHelpDialog,
  showChangePasswordDialog,
  showContactServiceDialog,
  closeAllDialogs,
} = useSettingsDialogs()

// 监听路由变化，退回登录页时关闭所有弹窗
watch(
  () => route.name,
  newRouteName => {
    if (newRouteName === 'login') {
      closeAllDialogs()
    }
  }
)

// 判断是否为登录页
const isLoginPage = computed(() => route.name === 'login')

/**
 * 更新 PC 端弹窗缩放 CSS 变量到 :root
 * el-dialog 渲染到 body 上，需要通过 :root CSS 变量传递
 */
const updatePcDialogScale = () => {
  if (isPCMode.value) {
    document.documentElement.style.setProperty('--pc-dialog-scale', String(pcScale.value))
    document.documentElement.classList.add('pc-dialog-scale-enabled')
  } else {
    document.documentElement.style.removeProperty('--pc-dialog-scale')
    document.documentElement.classList.remove('pc-dialog-scale-enabled')
  }
}

// 监听缩放比例变化，更新 CSS 变量
watch(pcScale, updatePcDialogScale, { immediate: true })

// 判断是否显示设置侧边栏 (在所有页面显示,除了登录页)
const showSettingsSidebar = computed(() => {
  return !isLoginPage.value
})

// PWA 安装教程弹窗显示状态
const showPWAGuide = ref(false)

// 页面重渲染 key (用于 iOS Safari 从其他标签页切换回来时强制重渲染)
const pageRenderKey = ref(0)

// 版本更新弹窗引用
const versionUpdateDialogRef = ref<InstanceType<typeof VersionUpdateDialog> | null>(null)

// 当前待更新的版本号
let pendingVersion = ''

/**
 * 处理版本更新回调 (从 configLoader 触发)
 */
const onVersionUpdate = (newVersion: string) => {
  pendingVersion = newVersion
  versionUpdateDialogRef.value?.show(newVersion)
}

/**
 * 处理用户点击"立即刷新"
 */
const handleVersionRefresh = () => {
  if (pendingVersion) {
    configHandleVersionRefresh(pendingVersion)
  }
}

// 初始化背景音乐 (只初始化,不自动播放)
useBgMusic()

// 初始化网络状态监听 (断网时自动跳转到登录页)
useNetworkStatus()

// 初始化用户心跳 (登录后每10秒发送一次心跳)
useHeartbeat()

/**
 * 检测是否为 iOS 设备 (iPhone 或 iPad)
 */
const isIOSDevice = (): boolean => {
  const ua = navigator.userAgent
  const isIOS = /iPad|iPhone|iPod/.test(ua)
  // iPad 在 iPadOS 13+ 会伪装成 Mac,需要额外检测触摸点
  const isIPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1
  return isIOS || isIPadOS
}

/**
 * 检测是否已安装为 PWA (多种检测方式)
 */
const isPWAInstalled = (): boolean => {
  // 方法 1: 检测 display-mode
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches
  if (isStandalone) {
    return true
  }

  // 方法 2: 检测 iOS Safari 的 standalone 模式
  // @ts-expect-error - standalone 是 iOS Safari 的非标准属性
  if (window.navigator.standalone === true) {
    return true
  }

  // 方法 3: 检测 URL 参数 (有些 PWA 会添加特殊参数)
  const urlParams = new URLSearchParams(window.location.search)
  if (urlParams.get('source') === 'pwa' || urlParams.get('mode') === 'standalone') {
    return true
  }

  return false
}

/**
 * 检查是否显示 PWA 安装教程
 * 仅在以下条件同时满足时显示:
 * 1. 当前是 iOS 设备 (iPhone 或 iPad)
 * 2. 未安装为 PWA (不在独立模式下)
 * 3. 本次会话未显示过 (sessionStorage 未标记)
 * 4. 当前是竖屏模式
 */
const checkPWAGuide = () => {
  // 条件 1: 检测是否为 iOS 设备
  if (!isIOSDevice()) {
    return
  }

  // 条件 2: 检测是否已安装为 PWA
  if (isPWAInstalled()) {
    return
  }

  // 条件 3: 检测本次会话是否已显示过
  const hasShown = sessionStorage.getItem('pwa-guide-shown')
  if (hasShown) {
    return
  }

  // 条件 4: 检测是否为竖屏模式
  const isVertical = window.innerWidth < window.innerHeight
  if (!isVertical) {
    return
  }

  // 延迟 1 秒显示,避免影响首次加载体验
  setTimeout(() => {
    showPWAGuide.value = true
  }, 1000)
}

/**
 * 处理页面刷新 - 设置标记用于清除保存的用户名密码
 * 在任何页面刷新时都会触发，登录页面会检测这个标记
 */
const handleBeforeUnload = () => {
  sessionStorage.setItem('pageUnloading', 'true')
}

/**
 * 视讯正片页面 (lobby/home/game) 刷新或关闭标签页时的兜底:
 *
 * 1. beforeunload 里弹浏览器原生确认框, 提醒玩家"游戏正在进行中"——注意文案
 *    是浏览器自己的固定文案 (Chrome/Firefox/Safari 出于防滥用早已统一, 网页
 *    只能决定弹不弹, 决定不了弹窗长什么样), 这里只负责触发。
 * 2. 真正的退出通知放在 pagehide (而不是等 beforeunload 里"点确认"之后再调):
 *    浏览器允许用户点确认就立刻销毁页面, 不会等一个异步的加密请求跑完;
 *    等页面真的开始卸载 (pagehide) 时用 `keepalive: true` 的 fetch 尽力把
 *    退出请求发出去, 发不发得出去、后端收不收得到都不保证, 只是尽力而为
 *    (行业标准做法, 比如 Google Docs 关闭前保存草稿也是这个思路)。
 * 3. event.persisted 为 true 代表页面进了 bfcache (比如跳去别的站再点后退
 *    就能整页恢复), 不是真的卸载, 这种情况不能退出视讯会话。
 */
const handleLiveBeforeUnload = (event: BeforeUnloadEvent) => {
  if (!LIVE_CONTENT_ROUTE_NAMES.has(route.name as string)) return
  event.preventDefault()
  event.returnValue = ''
}

const handleLivePageHide = (event: PageTransitionEvent) => {
  if (event.persisted) return
  if (!LIVE_CONTENT_ROUTE_NAMES.has(route.name as string)) return
  import('@/composables/useLiveGamePlay').then(({ useLiveGamePlay }) => {
    useLiveGamePlay()
      .exitLive({ keepalive: true })
      .catch(() => {})
  })
}

/**
 * 处理页面可见性变化
 * iOS Safari 关闭其他标签页时，工具栏会收起导致视口高度变化
 * 通过更新 pageRenderKey 强制整个页面重新渲染以适应新尺寸
 */
const handleVisibilityChange = () => {
  if (document.visibilityState === 'visible') {
    // 延迟执行，等待视口尺寸稳定
    setTimeout(() => {
      pageRenderKey.value++
    }, 300)
  }
}

/**
 * 阻止 iOS Safari 右滑返回手势
 * 通过监听触摸事件，在屏幕左边缘区域阻止默认滑动行为
 * 注意：登录页不阻止，允许用户正常返回
 */
let touchStartX = 0

const handleTouchStart = (e: TouchEvent) => {
  // 登录页不阻止
  if (route.name === 'login') {
    return
  }
  // 记录触摸起始位置
  touchStartX = e.touches[0]?.clientX ?? 0
}

const handleTouchMove = (e: TouchEvent) => {
  // 登录页不阻止
  if (route.name === 'login') {
    return
  }
  // 如果触摸起点在左边 30px 范围内，且向右滑动，阻止默认行为
  if (touchStartX < 30) {
    const currentX = e.touches[0]?.clientX ?? 0
    // 向右滑动 (currentX > touchStartX)
    if (currentX > touchStartX) {
      e.preventDefault()
    }
  }
}

/**
 * 应用初始化
 * 从 IndexedDB 恢复用户设置
 */
onMounted(async () => {
  // 检查是否是刷新页面（上次页面卸载时设置了标记）
  // 这个逻辑放在全局入口，确保无论从哪个页面刷新都能正确清除凭证
  const wasUnloading = sessionStorage.getItem('pageUnloading')
  if (wasUnloading) {
    // 清除标记
    sessionStorage.removeItem('pageUnloading')
    // 清除保存的用户名密码
    sessionStorage.removeItem('lastLoginUsername')
    sessionStorage.removeItem('lastLoginPassword')
  }

  // 监听页面卸载事件（刷新/关闭时触发）
  window.addEventListener('beforeunload', handleBeforeUnload)
  // 视讯正片页面专属: 刷新/关闭确认 + 尽力退出通知 (见函数注释)
  window.addEventListener('beforeunload', handleLiveBeforeUnload)
  window.addEventListener('pagehide', handleLivePageHide)

  // 监听页面可见性变化 (iOS Safari 关闭其他标签页时视口高度变化)
  document.addEventListener('visibilitychange', handleVisibilityChange)

  // 监听窗口大小变化 (PC 端弹窗缩放)
  window.addEventListener('resize', handleWindowResize)

  // 阻止 iOS Safari 右滑返回: 监听触摸事件
  document.addEventListener('touchstart', handleTouchStart, { passive: true })
  document.addEventListener('touchmove', handleTouchMove, { passive: false })

  // 设置版本更新回调
  setVersionUpdateCallback(onVersionUpdate)

  // 恢复用户设置
  await settingsStore.restoreFromCache()

  // 恢复登录状态（检查 localStorage 中的 token）
  await tryRestoreLogin()

  // 预测市场: 发现本地残留的 session_id (刷新页面/重新打开标签页) 就用 relaunch
  // 换新地址续上, 不重新带入资金; 必须放在 tryRestoreLogin() 之后, relaunch 需要
  // 登录态的 JWT
  tryRecoverPmSession()

  // Telegram 环境自动登录
  await tryTelegramAutoLogin()

  // 检查是否显示 PWA 安装教程
  checkPWAGuide()

  // 启用反调试保护 (仅生产环境)
  // setupDevToolsProtection()
})

/**
 * 尝试恢复登录状态
 * 检查 localStorage 中的 token，如果有效则恢复登录状态
 */
const tryRestoreLogin = async () => {
  const { isLoggedIn, getUserInfo } = await import('@/services/api/auth')

  // 检查本地是否有有效的 token
  if (!isLoggedIn()) {
    return
  }

  console.log('[App] 检测到有效 token，尝试恢复登录状态...')

  // 获取用户信息来验证 token 并恢复状态
  const result = await getUserInfo()

  if (result.success && result.data) {
    const userStore = useUserStore()
    userStore.memberId = result.data.uid || ''
    userStore.userName = result.data.nickname || ''
    userStore.isLoggedIn = true

    await userStore.persistUserInfo()
    console.log('[App] 登录状态恢复成功')

    // 加载大厅数据（包括 currencies）
    const lobbyStore = useLobbyStore()
    lobbyStore.loadLobbyData()
  } else {
    console.warn('[App] 登录状态恢复失败:', result.error)
    // token 无效，清除它
    const { logout } = await import('@/services/api/auth')
    logout()
  }
}

/**
 * 尝试 Telegram 自动登录
 * 检测环境 -> 加载SDK -> 自动登录
 */
const tryTelegramAutoLogin = async () => {
  const { isTelegramEnv } = await import('@/utils/telegram')

  // 非 Telegram 环境，跳过
  if (!isTelegramEnv()) {
    return
  }

  console.log('[App] 检测到 Telegram 环境，尝试自动登录...')

  const { autoLoginWithTelegram } = await import('@/services/api/auth')
  const userStore = useUserStore()

  const result = await autoLoginWithTelegram()

  if (result.success && result.data) {
    // 更新用户状态
    userStore.memberId = result.data.uid
    userStore.isLoggedIn = true
    userStore.userType = '5'

    // 获取 Telegram 用户名用于显示
    const { getTelegramUser } = await import('@/utils/telegram')
    const tgUser = getTelegramUser()
    if (tgUser) {
      userStore.userName = tgUser.first_name + (tgUser.last_name ? ' ' + tgUser.last_name : '')
    }

    await userStore.persistUserInfo()
    console.log('[App] Telegram 自动登录成功')

    // 加载大厅数据（包括 currencies）
    const lobbyStore = useLobbyStore()
    lobbyStore.loadLobbyData()
  } else {
    console.warn('[App] Telegram 自动登录失败:', result.error)
  }
}

/**
 * 组件卸载 - 清除监听器
 */
onUnmounted(() => {
  window.removeEventListener('beforeunload', handleBeforeUnload)
  window.removeEventListener('beforeunload', handleLiveBeforeUnload)
  window.removeEventListener('pagehide', handleLivePageHide)
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  document.removeEventListener('touchstart', handleTouchStart)
  document.removeEventListener('touchmove', handleTouchMove)
  window.removeEventListener('resize', handleWindowResize)
})
</script>

<style scoped lang="scss">
// 对话框通用深色主题样式
:deep(.el-dialog) {
  background-color: #22222e;
  border: 1px solid #3a3a4a;
  color: #ffffff;
  margin: 0 auto;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  // 默认样式 (手机竖屏)
  .el-dialog__header {
    padding-bottom: 10px;
  }

  .el-dialog__title {
    color: #fff;
    font-size: 28px; // 手机竖屏默认
  }

  .el-dialog__body {
    padding: 20px;
  }

  // 手机横屏 (触屏设备, 高度 ≤ 500px)
  @media (max-height: 500px) and (orientation: landscape) and (hover: none) and (pointer: coarse) {
    .el-dialog__header {
      padding: 10px 20px;
    }

    .el-dialog__title {
      font-size: 24px;
    }

    .el-dialog__body {
      padding: 10px 20px;
    }
  }

  // iPad 竖屏 (触屏设备, 768px ≤ 宽度 < 1024px)
  @media (min-width: 768px) and (max-width: 1023px) and (hover: none) and (pointer: coarse) {
    .el-dialog__header {
      padding-bottom: 16px;
    }

    .el-dialog__title {
      font-size: 32px;
    }

    .el-dialog__body {
      padding: 20px;
    }
  }

  // iPad 横屏 (触屏设备, 宽度 ≥ 1024px, 高度 600-900px)
  @media (min-width: 1024px) and (orientation: landscape) and (min-height: 600px) and (hover: none) and (pointer: coarse) {
    .el-dialog__header {
      padding-bottom: 20px;
    }

    .el-dialog__title {
      font-size: 36px;
    }

    .el-dialog__body {
      padding: 24px;
    }
  }

  // PC 端 (鼠标设备)
  @media (min-width: 1024px) and (hover: hover) and (pointer: fine) {
    .el-dialog__header {
      padding-bottom: 16px;
    }

    .el-dialog__title {
      font-size: 20px;
    }

    .el-dialog__body {
      padding: 20px;
    }

    .el-dialog__headerbtn {
      top: 16px;
      right: 28px;
      width: 32px;
      height: 32px;

      .el-dialog__close {
        font-size: 40px !important;
      }
    }
  }
}

// 横版全屏对话框样式
// 横版模式下 isLandscapeMode=true, top="0", 使用 100dvh
:deep(.landscape-fullscreen) {
  height: 99dvh;
  margin: 0 auto;
}

// 竖屏全屏对话框样式
// 竖屏模式下 isLandscapeMode=false, top="10%", 使用 90dvh
:deep(.portrait-fullscreen) {
  height: 89dvh;
  margin: 0 auto;
}

// PC 端全屏对话框样式
// PC 端固定高度 1000px
:deep(.pc-fullscreen) {
  height: 1000px;
  margin: 0 auto;
}
// ===================== PC 端弹窗缩放样式 =====================
// 当 --pc-dialog-scale 变量存在时，对 el-dialog 应用缩放
</style>

<style lang="scss">
// 全局样式 (不加 scoped)，用于 el-dialog 缩放
// el-dialog 渲染到 body 上，需要全局样式

// PC 端 el-dialog 弹窗缩放
// 当 html 有 pc-dialog-scale-enabled 类时，对所有 el-dialog 应用缩放
// 排除带有 no-pc-scale 类的弹窗 (如协议弹窗，已在 16:9 容器内)
:root.pc-dialog-scale-enabled .el-overlay .el-dialog:not(.no-pc-scale) {
  transform: translate(-50%, -50%) scale(var(--pc-dialog-scale));
}
</style>

<style scoped lang="scss">
:deep(.report) {
  padding: 0 20px !important;

  // Element Plus 滚动条样式 - 更粗
  // 横向滚动条
  .el-scrollbar__bar.is-horizontal {
    height: 18px !important;
  }

  // 竖向滚动条
  .el-scrollbar__bar.is-vertical {
    width: 18px !important;
  }

  // 关闭按钮样式 (因为 padding-top 为 0,需要调整位置)
  // 默认样式 (手机竖屏)
  .el-dialog__headerbtn {
    top: 28px;
    right: 28px;
    width: 28px;
    height: 28px;
    z-index: 1000;

    .el-dialog__close {
      font-size: 32px;
    }
  }

  // 手机横屏 (触屏设备, max-height <= 500px)
  @media (max-height: 500px) and (orientation: landscape) and (hover: none) and (pointer: coarse) {
    .el-dialog__headerbtn {
      top: 36px;
      right: 56px;
      width: 36px;
      height: 36px;

      .el-dialog__close {
        font-size: 56px;
      }
    }
  }

  // iPad 竖屏 (触屏设备, 768px <= width < 1024px)
  @media (min-width: 768px) and (max-width: 1023px) and (hover: none) and (pointer: coarse) {
    .el-dialog__headerbtn {
      top: 28px;
      right: 36px;
      width: 40px;
      height: 40px;

      .el-dialog__close {
        font-size: 48px;
      }
    }
  }

  // iPad 横屏 (触屏设备, width >= 1024px, 高度 600-900px)
  @media (min-width: 1024px) and (orientation: landscape) and (min-height: 600px) and (hover: none) and (pointer: coarse) {
    .el-dialog__headerbtn {
      top: 32px;
      right: 36px;
      width: 48px;
      height: 48px;

      .el-dialog__close {
        font-size: 56px;
      }
    }
  }

  // PC 端 (鼠标设备)
  @media (min-width: 1024px) and (hover: hover) and (pointer: fine) {
    .el-dialog__headerbtn {
      top: 28px;
      right: 48px;
      width: 32px;
      height: 32px;

      .el-dialog__close {
        font-size: 60px !important;
      }
    }
  }
}
</style>
